/**
 * Password Reset API Client
 *
 * Public endpoints for self-service password recovery.
 * Pattern mirrors invitationApi.ts for consistency.
 */

import { fetchPublic } from './http-client';

const API_BASE = '/api/v1';

export interface InitiateResetResponse {
  success: boolean;
  message: string;
}

export interface ValidateResetResponse {
  valid: boolean;
  email: string;
  fullName: string;
  hoursRemaining: number;
}

export interface CompleteResetResponse {
  success: boolean;
  message: string;
}

/**
 * Request a password reset email (PUBLIC - no auth required)
 * Always returns success to prevent email enumeration
 */
export async function initiatePasswordReset(email: string): Promise<InitiateResetResponse> {
  const response = await fetchPublic(`${API_BASE}/auth/password-reset/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Error ${response.status}`);
  }

  const data = await response.json();
  return data.data;
}

/**
 * Validate a reset token (PUBLIC - no auth required)
 */
export async function validateResetToken(token: string): Promise<ValidateResetResponse> {
  const response = await fetchPublic(`${API_BASE}/auth/password-reset/validate?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorCode = errorData.error?.code || 'UNKNOWN_ERROR';
    const errorMessage = errorData.error?.message || `Error ${response.status}`;
    const error = new Error(errorMessage);
    (error as any).code = errorCode;
    throw error;
  }

  const data = await response.json();
  return data.data;
}

/**
 * Complete password reset with new password (PUBLIC - no auth required)
 */
export async function completePasswordReset(token: string, newPassword: string): Promise<CompleteResetResponse> {
  const response = await fetchPublic(`${API_BASE}/auth/password-reset/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorCode = errorData.error?.code || 'UNKNOWN_ERROR';
    const errorMessage = errorData.error?.message || `Error ${response.status}`;
    const error = new Error(errorMessage);
    (error as any).code = errorCode;
    throw error;
  }

  const data = await response.json();
  return data.data;
}
